import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import { SettingsCard } from "../../../../../components/card/SettingsCard";
import emptySubtasksImg from "../../../../../components/svg/emptySubtasks.svg";
import PlusSvg from "../../../../../components/svg/PlusSvg";
import { TaskTemplateSubtasksSectionProps } from "../model/taskTemplateEditor.types";
import { getSubtaskBudgetValue } from "../model/taskTemplateEditor.utils";
import { Subtask } from "./Subtask";
import { SubtaskForm } from "./SubtaskForm";

export const TaskTemplateSubtasksSection: React.FC<
  TaskTemplateSubtasksSectionProps
> = ({
  isEditing,
  subtasks,
  isOpenAdd,
  editingSubtask,
  automationTooltipsBySubtaskId,
  deletingAutomationIds,
  onDragEnd,
  onOpenAddSubtask,
  onCloseSubtaskForm,
  onSaveLocalSubtask,
  onEditDraftSubtask,
  onDeleteDraftSubtask,
  onEditTemplateSubtask,
  onAssignUser,
  onBudgetChange,
  onDueChange,
  onAddAutomation,
  onDeleteAutomation,
}) => (
  <SettingsCard extraClasses={"p-6 min-h-[500px]"}>
    <div className={"mb-4 flex items-center gap-2"}>
      <div className={"text-xl font-semibold text-[#2B333C]"}>Subtasks</div>

      <div
        className={
          "flex h-6 w-fit min-w-[24px] items-center justify-center rounded-full bg-[#E4E9F2] px-2 text-xs text-[#3F5266]"
        }>
        {subtasks.length}
      </div>
    </div>

    {subtasks.length > 0 || isOpenAdd ? (
      <div>
        {subtasks.length > 0 && (
          <div
            className={
              "mb-2 grid grid-cols-[minmax(0,1fr)_128px_208px_114px_32px] gap-4 px-3 text-sm font-semibold text-[#2B333CCC]"
            }>
            <div />
            <div className={"text-center"}>Time budget</div>
            <div
              className={"text-center"}
              title={
                "Due date of the subtask when the template is added to a work item. Empty = the work's due date."
              }>
              Due
            </div>
            <div className={"text-end"}>Assignee</div>
            <div />
          </div>
        )}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={"template"}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {subtasks.map((subtask, index) => (
                  <Draggable
                    key={subtask.id}
                    draggableId={subtask.id}
                    index={index}>
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={dragProvided.draggableProps.style}
                        className={"mb-2"}>
                        <Subtask
                          subtask={subtask}
                          order={index + 1}
                          isDraft={!isEditing || subtask.isNew}
                          onEditDraft={onEditDraftSubtask}
                          onDeleteDraft={onDeleteDraftSubtask}
                          onEditSubtask={onEditTemplateSubtask}
                          onAssignUser={onAssignUser}
                          budgetedTime={getSubtaskBudgetValue(subtask)}
                          onBudgetChange={onBudgetChange}
                          dueDays={subtask.dueDays}
                          dueAfterStart={subtask.dueAfterStart}
                          onDueChange={onDueChange}
                          automationTooltip={automationTooltipsBySubtaskId.get(
                            subtask.id,
                          )}
                          isAutomationDeleting={Boolean(
                            subtask.defaultTemplateTaskAutomation?.[0]?.id &&
                              deletingAutomationIds.has(
                                subtask.defaultTemplateTaskAutomation[0].id,
                              ),
                          )}
                          onAddAutomation={onAddAutomation}
                          onDeleteAutomation={onDeleteAutomation}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {isOpenAdd && (
          <SubtaskForm
            defaultValue={editingSubtask || undefined}
            order={
              editingSubtask
                ? subtasks.findIndex((item) => item.id === editingSubtask.id) +
                  1
                : subtasks.length + 1
            }
            onClose={onCloseSubtaskForm}
            isLocalMode
            onSubmitLocal={onSaveLocalSubtask}
          />
        )}

        {!isOpenAdd && (
          <button
            type={"button"}
            onClick={onOpenAddSubtask}
            className={
              "transitions-all flex h-10 items-center justify-center gap-2 rounded-lg border border-[#6938EF] bg-white px-4 text-sm font-medium text-[#6938EF] duration-300 hover:shadow-settings-purple"
            }>
            <PlusSvg />
            <span>Add Subtask</span>
          </button>
        )}
      </div>
    ) : (
      <div className={"flex justify-center"}>
        <div className={"flex w-[420px] flex-col items-center text-center"}>
          <img
            src={emptySubtasksImg}
            alt={"Empty Subtasks List"}
            className={"mb-2"}
          />

          <div className={"mb-2 text-2xl font-semibold text-[#1D2C3B]"}>
            Add Subtasks
          </div>

          <div className={"mb-6 text-base text-[#3F5266]"}>
            Add and organize subtasks for this template. They make it easier to
            plan, assign, and track smaller actions.
          </div>

          <button
            type={"button"}
            onClick={onOpenAddSubtask}
            className={
              "transitions-all flex h-10 items-center justify-center gap-2 rounded-lg border border-[#6938EF] bg-white px-4 text-sm font-medium text-[#6938EF] duration-300 hover:shadow-settings-purple"
            }>
            <PlusSvg />
            <span>Add Subtask</span>
          </button>
        </div>
      </div>
    )}
  </SettingsCard>
);
